
import React, { useContext, useEffect, useState } from "react";
import { AuthLayout } from "./AuthLayout";
import { Alert, AlertDescription } from "./ui/alert";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Lock, XCircle } from "lucide-react";
import { Label } from "@radix-ui/react-label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { on } from "events";

import { useAuth } from "./AuthContext";

interface ResetPasswordProps {
  onBackToSignIn: () => void;
}
export const ResetPasswordPage : React.FC<ResetPasswordProps> = ({onBackToSignIn}) => {

  const {signOut}=useAuth()

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);


  useEffect(() => {
    const urlToken = new URLSearchParams(window.location.search).get("token");
    setToken(urlToken);
  }, []);

  const rules = [
    { test: (p: string) => p.length >= 8, label: "Must be at least 8 characters" },
    { test: (p: string) => /[A-Z]/.test(p), label: "Must contain at least one uppercase letter" },
    { test: (p: string) => /[a-z]/.test(p), label: "Must contain at least one lowercase letter" },
    { test: (p: string) => /[0-9]/.test(p), label: "Must contain at least one number between 0 & 9" },
    { test: (p: string) => /[^A-Za-z0-9]/.test(p), label: "Must contain one special character" },
  ];

  const validate = (): string | null => {
    if (password !== confirm) return "New Password and Confirm Password must be identical.";
    if (!rules.every(r => r.test(password))) return "Password does not meet requirements.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }

    try {
     setLoading(true);
        const response = await fetch(
        `${import.meta.env.VITE_API_URL}auth/password/reset/confirm`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({token,  password,}),
        },
      );

      const data = await response.json();
      
      if (!response.ok) throw new Error(data.message || "Request failed");

      toast.success(data?.message || "Password reset successfully");
      window.history.replaceState({}, document.title, "/");
      signOut();
      onBackToSignIn();

    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset Password">
        <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="destructive" className='bg-red-50 border-red-200'>
            <AlertCircle className="w-4 h-4" />
            <AlertDescription className='text-red-600'>{error}</AlertDescription>
          </Alert>
        )}

  
         <div className="space-y-2">
            <Label htmlFor="password">New Password*</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="New password"
                value={password}
                onChange={(e) =>{ 
                  setPassword(e.target.value);
                  {error && setError('')};
                }
                }
                className="pl-10 pr-10"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
                  {/* Rules list */}
          <ul className="mt-2 space-y-1 text-sm">
            {rules.map((rule, i) => {
              const passed = rule.test(password);
              return (
                <li key={i} className="flex items-center gap-2">
                  {passed ? (
                    <CheckCircle2 className="text-green-500 w-4 h-4" />
                  ) : (
                    <CheckCircle2 className="text-gray-400 w-4 h-4" />
                  )}
                  <span className={passed ? "text-green-600" : "text-gray-500"}>
                    {rule.label}
                  </span>
                </li>
              );
            })}
          </ul>


          <div className="space-y-2">
            <Label htmlFor="password">Confirm Password*</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  {error && setError('')};}
                }
                className="pl-10 pr-10"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={loading}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
   
          <Button
          className="w-full"
            type="submit"
            disabled={loading || (!rules.every(r => r.test(password))) || confirm.length === 0}
          >
           Reset Password
          </Button>

      </form>
    </AuthLayout>
  );
}
